import { getGatewayAnalysisAction, getUserAnalysisAction } from 'actions/SalesAnalysisActions';
import { Input } from 'components/custom/input';
import { ReportSection } from 'components/custom/ReportSection';
import SideFilter from 'components/custom/sideFilter';
import { CustomTable } from 'components/custom/table/CustomTable';
import { TopHeader } from 'components/layouts/page/topHeader';
import { TIME_ZONE_FORMAT } from 'constants/format';
import {
  agent,
  convertSaleAnalysisGroupToOptions,
  gatewayProvider,
  isAgent,
  isGatewayProvider,
  SALES_ANALYSIS_GROUP,
} from 'constants/Roles';
import { useTranslate } from 'context/TranslateContext';
import { getNDaysEarlier } from 'helpers/functions/getDays';
import { isActionBusy } from 'helpers/functions/isActionBusy';
import { useSwal } from 'helpers/sweetalert';
import moment from 'moment';
import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LoadOptions } from 'react-select-async-paginate';
import { FunctionDispatch, GlobalTypes } from 'redux/types';
import { getCompanyList } from 'services/API/Company';
import { getGatewaySalesAnalysis, getUserSalesAnalysis } from 'services/API/Sales_Analysis';
import type {
  GetCompanyAnalysisResponse,
  SalesAnalysisSummary,
} from 'services/API/Sales_Analysis/interface';
import { SummaryTable } from './SummaryTable';

const initialState = {
  start_date: new Date(getNDaysEarlier(7)),
  end_date: new Date(),
  group_by: '',
  order_by: '',
  limit: 10,
  export: '',
  gateway_id: '',
  full_list: 0,
};

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_DURATION_MS = 120000;

type SalesAnalysisParams = Record<string, string | number | boolean>;

type AnalysisTarget =
  | {
      type: 'gateway';
      id: string;
    }
  | {
      type: 'user';
      id: string;
    };

interface ReportSectionHandle {
  loadReports: () => Promise<void>;
}

interface PollRequest {
  params: SalesAnalysisParams;
  requestId: number;
}

const valueToLabel = (value: any) => {
  if (!value) {
    return { label: '', value: null };
  }
  value.toString = () => value.id;
  return {
    label: value.name,
    value: value,
  };
};

export const SalesAnalysis = () => {
  const {
    auth: { user },
    data: {
      analysis: {
        sale: { data, pagination, group_by: saleGroupBy },
      },
    },
  } = useSelector((state: GlobalTypes.RootState) => state);

  const dispatch = useDispatch<FunctionDispatch>();
  const reportSectionRef = useRef<ReportSectionHandle | null>(null);
  const requestIdRef = useRef(0);
  const pollStartedAtRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);
  const [Swal] = useSwal();
  const { translate, language } = useTranslate();

  const [filter, setFilter] = useState(initialState);
  const [currentPage, setCurrentPage] = useState(1);
  const [summaryData, setSummaryData] = useState<SalesAnalysisSummary>();
  const [isExportDropdownLoading, setIsExportDropdownLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [pollRequest, setPollRequest] = useState<PollRequest | null>(null);
  const [selectedCompanies, setSelectedCompanies] = useState<Array<any>>([]);
  const actionBusy = isActionBusy(isPageLoading, isExportDropdownLoading);

  const {
    start_date,
    end_date,
    group_by,
    order_by,
    limit,
    full_list,
    gateway_id,
    export: exportType,
  } = filter;

  const searchCompany: LoadOptions<any, any> = useCallback(
    async (keyword, loadedOptions, additional) => {
      const selectedCompanyIdArr = selectedCompanies.map((c) => c.id);
      const page = additional?.page || 1;
      const result = await getCompanyList({ page, keyword: keyword });
      const hasMore = !!result.meta.pagination?.links.next;
      const companies = result.data.filter((c) => !selectedCompanyIdArr.includes(c.id));

      return {
        options: companies.map(valueToLabel),
        hasMore,
        additional: {
          page: page + 1,
        },
      };
    },
    [selectedCompanies],
  );

  const getGatewayOptions = useCallback(() => {
    if (!user.gateways) {
      return [];
    }
    const allGatewayOption = [
      {
        label: translate('all_gateways'),
        value: '',
      },
    ];

    const userGatewayOptions = user.gateways.map((gateway) => ({
      label: `${translate('gateway')}: ${translate(gateway.name)}`,
      value: gateway.gid,
    }));

    return [...allGatewayOption, ...userGatewayOptions];
  }, [translate, user.gateways]);

  const onFilter = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (currentPage === 1) {
      getInitialData();
    } else {
      setCurrentPage(1);
    }
  };
  const resetFilter = () => {
    setFilter({
      ...initialState,
      group_by: SALES_ANALYSIS_GROUP[user.role]?.[0],
    });
    setSelectedCompanies([]);
  };
  const onFilterDateChange = (date: Date, name: string) => {
    setFilter({
      ...filter,
      [name]: date,
    });
  };
  const onFilterSelectChange = (name: string) => (value: string) => {
    setFilter({
      ...filter,
      [name]: value,
    });
  };

  const getParams = useCallback((): SalesAnalysisParams => {
    const params: SalesAnalysisParams = {
      start_date: moment(start_date).startOf('day').format(TIME_ZONE_FORMAT),
      end_date: moment(end_date).endOf('day').format(TIME_ZONE_FORMAT),
      group_by: group_by || SALES_ANALYSIS_GROUP[user.role]?.[0] || '',
    };

    if (order_by) {
      params.order_by = order_by;
    }
    if (limit) {
      params.limit = limit;
    }
    if (exportType) {
      params.export = exportType;
    }
    if (full_list) {
      params.full_list = full_list;
    }
    if (gateway_id) {
      params.gateway_id = gateway_id;
    }
    if (selectedCompanies.length) {
      params.company_id = selectedCompanies.map((c) => c.id).join(',');
    }
    if (currentPage) {
      params.page = currentPage;
    }

    return params;
  }, [
    currentPage,
    end_date,
    exportType,
    full_list,
    gateway_id,
    group_by,
    limit,
    order_by,
    selectedCompanies,
    start_date,
    user.role,
  ]);

  const getAnalysisTarget = useCallback(
    (params: SalesAnalysisParams): AnalysisTarget => {
      switch (user.role) {
        case gatewayProvider: {
          if (params.gateway_id) {
            const selectedGateway = user.gateways?.find(
              (gateway) => String(gateway.gid) === String(params.gateway_id),
            );
            if (!selectedGateway?.id) {
              throw new Error('No Analysis data provided for this gateway');
            }
            return { type: 'gateway', id: selectedGateway.id };
          }
          return { type: 'user', id: user.id };
        }
        case agent:
          return { type: 'user', id: user.id };
        default:
          throw new Error('No Analysis data provided for this role');
      }
    },
    [user.gateways, user.id, user.role],
  );

  const requestAnalysis = useCallback(
    (params: SalesAnalysisParams): Promise<GetCompanyAnalysisResponse> => {
      const target = getAnalysisTarget(params);
      if (target.type === 'gateway') {
        return dispatch(getGatewayAnalysisAction(target.id, params));
      }
      return dispatch(getUserAnalysisAction(target.id, params));
    },
    [dispatch, getAnalysisTarget],
  );

  const requestExportAnalysis = useCallback(
    (params: SalesAnalysisParams): Promise<GetCompanyAnalysisResponse> => {
      const target = getAnalysisTarget(params);
      if (target.type === 'gateway') {
        return getGatewaySalesAnalysis(target.id, params);
      }
      return getUserSalesAnalysis(target.id, params);
    },
    [getAnalysisTarget],
  );

  const resetPollingState = useCallback(() => {
    pollStartedAtRef.current = null;
    setPollRequest(null);
  }, []);

  const finishPageLoading = useCallback(() => {
    resetPollingState();
    setIsPageLoading(false);
  }, [resetPollingState]);

  const reachedPollingLimit = useCallback(() => {
    const now = Date.now();
    if (pollStartedAtRef.current === null) {
      pollStartedAtRef.current = now;
    }
    return now - pollStartedAtRef.current >= MAX_POLL_DURATION_MS;
  }, []);

  const getInitialData = useCallback(
    async (paramsOverride?: SalesAnalysisParams, isPolling = false) => {
      const data = paramsOverride || getParams();
      const requestId = isPolling ? requestIdRef.current : requestIdRef.current + 1;

      if (!isPolling) {
        requestIdRef.current = requestId;
        resetPollingState();
      }

      setIsPageLoading(true);

      try {
        const res = await requestAnalysis(data);

        if (!isMountedRef.current || requestId !== requestIdRef.current) {
          return;
        }

        if (!res.success) {
          finishPageLoading();
          await Swal.fire({
            icon: 'error',
            title: translate('analysis'),
            text: res.message || 'Oops',
          });
          return;
        }

        if (typeof res.progress === 'number' && res.progress < 100) {
          if (res.progress < 0 || reachedPollingLimit()) {
            finishPageLoading();
            await Swal.fire({
              icon: 'error',
              title: translate('analysis'),
              text: res.message || 'Oops',
            });
            return;
          }

          setPollRequest({
            params: data,
            requestId,
          });
          return;
        }

        setSummaryData({
          total_paid: res.total_paid,
          total_paid_amount: res.total_paid_amount,
          total_charge: res.total_charge,
          total_refund: res.total_refund,
          total_refund_amount: res.total_refund_amount,
          total_void: res.total_void,
          total_void_amount: res.total_void_amount,
          total_cancel: res.total_cancel,
          total_transactions: res.total_transactions,
          total_commission: res.total_commission,
          average_ticket: res.average_ticket,
        });
        finishPageLoading();
      } catch (error) {
        if (!isMountedRef.current || requestId !== requestIdRef.current) {
          return;
        }
        finishPageLoading();
        await Swal.fire({
          icon: 'error',
          title: translate('analysis'),
          text: error instanceof Error ? error.message : 'Oops',
        });
      }
    },
    [
      Swal,
      finishPageLoading,
      getParams,
      reachedPollingLimit,
      requestAnalysis,
      resetPollingState,
      translate,
    ],
  );
  const exportExcel = async () => {
    setIsExportDropdownLoading(true);
    const data = {
      ...getParams(),
      export: 'xlsx',
      asyncExport: true,
    };

    try {
      const res = await requestExportAnalysis(data);
      if (isMountedRef.current) {
        setIsExportDropdownLoading(false);
      }
      // ALICE-756: wait for the async export to become visible before refreshing reports.
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await reportSectionRef.current?.loadReports();

      if (!res.success) {
        await Swal.fire({
          icon: 'error',
          title: translate('export'),
          text: res.message || 'Oops',
        });
      }
    } catch (error) {
      if (isMountedRef.current) {
        setIsExportDropdownLoading(false);
      }
      await Swal.fire({
        icon: 'error',
        title: translate('export'),
        text: error instanceof Error ? error.message : 'Oops',
      });
    }
  };

  const detailHeader = () => {
    let id, name;

    switch (saleGroupBy) {
      case 'mcc':
        id = translate('mcc');
        name = translate('mcc_description');
        break;
      case 'store':
        id = translate('store_id');
        name = translate('store_name');
        break;
      case 'gateway':
        id = translate('gateway_name');
        name = translate('gateway_name');
        break;
      default:
      case 'company':
        id = translate('company_id');
        name = translate('company_name');
        break;
    }

    return (
      <tr>
        <th className="border-end border-top-0">{id}</th>
        <th className="border-end border-top-0">{name}</th>
        <th className="border-end border-top-0">{translate('total_paid')}</th>
        <th className="border-end border-top-0">{translate('total_paid_amount')}</th>
        <th className="border-end border-top-0">{translate('total_charge')}</th>
        <th className="border-end border-top-0">{translate('total_refund')}</th>
        <th className="border-end border-top-0">{translate('total_refund_amount')}</th>
        <th className="border-end border-top-0">{translate('total_cancel')}</th>
        <th className="border-end border-top-0">{translate('total_transactions')}</th>
        <th className="border-end border-top-0">{translate('total_commission')}</th>
        <th className="border-top-0">{translate('average_ticket')}</th>
      </tr>
    );
  };

  const detailBody = () => {
    let id_field: string, name_field: string;

    switch (saleGroupBy) {
      case 'mcc':
        id_field = 'mcc';
        name_field = language === 'zh' ? 'description_zh' : 'description';
        break;
      case 'company':
        id_field = 'company_id';
        name_field = 'company_name';
        break;
      case 'store':
        id_field = 'store_id';
        name_field = 'store_name';
        break;
      case 'gateway':
        id_field = 'gateway_id';
        name_field = 'gateway_name';
        break;
      default:
    }

    return data
      ? data.map((sale, index) => {
          const {
            total_paid,
            total_paid_amount,
            total_charge,
            total_refund,
            total_refund_amount,
            total_cancel,
            total_transactions,
            total_commission,
            average_ticket,
          } = sale;

          return (
            <tr key={index} className="cursor-pointer" onClick={() => sale}>
              <td>{sale[id_field]}</td>
              <td>{sale[name_field]}</td>
              <td>{total_paid}</td>
              <td>{total_paid_amount}</td>
              <td>{total_charge}</td>
              <td>{total_refund}</td>
              <td>{total_refund_amount}</td>
              <td>{total_cancel}</td>
              <td>{total_transactions}</td>
              <td>{total_commission}</td>
              <td>{average_ticket}</td>
            </tr>
          );
        })
      : [];
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!pollRequest || pollRequest.requestId !== requestIdRef.current) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      getInitialData(pollRequest.params, true);
    }, POLL_INTERVAL_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [getInitialData, pollRequest]);

  useEffect(() => {
    setFilter({
      ...filter,
      group_by: group_by ? group_by : SALES_ANALYSIS_GROUP[user.role]?.[0],
    });
    getInitialData();
    //eslint-disable-next-line
  }, [currentPage]);

  return (
    <>
      <TopHeader title={translate('analysis')}>
        <button
          className="header-button btn btn-success"
          disabled={actionBusy}
          onClick={() => exportExcel()}>
          {translate('generate_excel_report')}
        </button>
      </TopHeader>
      <div id="main-content" className="hide-scrollbar">
        <SummaryTable summaryData={summaryData} />
        <div className="mt-4"></div>
        <CustomTable
          totalColumn={11}
          renderHeading={detailHeader}
          pagination={pagination}
          setCurrentPage={setCurrentPage}
          renderData={detailBody}
          isLoading={isPageLoading}
          showOldDataWhenReload={false}
          allowHover
        />
      </div>
      <div id="side-content" className="hide-scrollbar">
        <SideFilter onFilter={onFilter} resetFilter={resetFilter} isLoading={actionBusy}>
          <Input
            type="date"
            value={start_date}
            legend={translate('from').toUpperCase()}
            name="start_date"
            onChange={onFilterDateChange}
            placeholder="YYYY-MM-DD"
          />
          <Input
            type="date"
            value={end_date}
            legend={translate('to').toUpperCase()}
            name="end_date"
            onChange={onFilterDateChange}
            placeholder="YYYY-MM-DD"
          />
          <Input
            type="react-select"
            legend={translate('group_by').toUpperCase()}
            options={convertSaleAnalysisGroupToOptions(SALES_ANALYSIS_GROUP[user.role], translate)}
            value={group_by}
            name="group_by"
            onChange={onFilterSelectChange('group_by')}
            menuPortalTarget={document.body}
          />
          {user.gateways?.length && (
            <Input
              type="react-select"
              legend={translate('gateway').toUpperCase()}
              options={getGatewayOptions()}
              value={gateway_id}
              name="gateway_id"
              onChange={onFilterSelectChange('gateway_id')}
              menuPortalTarget={document.body}
            />
          )}
          {(isGatewayProvider(user.role) || isAgent(user.role)) && (
            <Input
              type="react-select-async"
              value={selectedCompanies.map(valueToLabel)}
              loadOptions={searchCompany}
              onChange={setSelectedCompanies}
              legend={translate('company').toUpperCase()}
              name="company_id"
              placeholder={translate('all_companies')}
              isMulti
              menuPortalTarget={document.body}
            />
          )}
        </SideFilter>
        <ReportSection ref={reportSectionRef} type="sales-analysis" />
      </div>
    </>
  );
};
